import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Cpu, Terminal, Ban } from "lucide-react";
import { JobsApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { PageHeader } from "../components/PageScaffold";
import { relativeTime, formatDuration, formatBytes } from "../lib/format";
import { cn } from "../lib/cn";
import type { JobMeta } from "../lib/types";

const STATUSES = ["running", "completed", "failed", "killed", "timeout", "cancelled"] as const;

export default function Jobs() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("");
  const [agent, setAgent] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: jobs = [], isLoading, error } = useQuery({
    queryKey: qk.jobs.list({ status, agent }),
    queryFn: () => JobsApi.list({ status: status || undefined, requested_by: agent || undefined, limit: 500 }),
    refetchInterval: 5_000,
  });

  const agents = Array.from(new Set(jobs.map((j) => j.requested_by))).sort();
  const runningCount = jobs.filter((j) => j.status === "running").length;
  const failedCount = jobs.filter((j) => j.status === "failed" || j.status === "timeout").length;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Jobs"
        icon={Cpu}
        subtitle={
          jobs.length
            ? `${jobs.length} recent · ${runningCount} running · ${failedCount} failed`
            : "bridge-owned background jobs (run_job)"
        }
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<JobMeta>
          items={jobs}
          itemKey={(j) => j.id}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={isLoading}
          error={error as Error | null}
          itemHeight={72}
          toolbar={
            <div className="flex items-center gap-2 px-3 py-2">
              <select value={agent} onChange={(e) => setAgent(e.target.value)} className="h-7 text-xs w-32">
                <option value="">All agents</option>
                {agents.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-7 text-xs w-32">
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <span className="ml-auto text-[11px] text-fg-subtle font-mono">{jobs.length}</span>
            </div>
          }
          emptyState={<EmptyState icon={<Cpu className="size-8" />} title="No background jobs yet" description="Agents start these with the run_job tool." />}
          renderItem={({ item }) => <JobRow j={item} />}
          renderDetail={(j) => <JobDetail id={j.id} onKilled={() => qc.invalidateQueries({ queryKey: ["jobs"] })} />}
        />
      </div>
    </div>
  );
}

function statusTone(s: string): "neutral" | "accent" | "success" | "warning" | "danger" {
  switch (s) {
    case "running": return "accent";
    case "completed": return "success";
    case "failed": return "danger";
    case "timeout": return "danger";
    case "killed": return "warning";
    case "cancelled": return "neutral";
    default: return "neutral";
  }
}

function JobRow({ j }: { j: JobMeta }) {
  const isRunning = j.status === "running";
  const dur = j.ended_at
    ? formatDuration(new Date(j.ended_at).getTime() - new Date(j.started_at).getTime())
    : isRunning
      ? formatDuration(Date.now() - new Date(j.started_at).getTime()) + "…"
      : "—";
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <span
          className={cn(
            "size-1.5 rounded-full flex-shrink-0",
            j.status === "running" && "bg-accent animate-pulse-dot",
            j.status === "completed" && "bg-success",
            (j.status === "failed" || j.status === "timeout") && "bg-danger",
            j.status === "killed" && "bg-warning",
            j.status === "cancelled" && "bg-fg-subtle",
          )}
        />
        <span className="text-sm font-medium text-fg truncate">{j.label}</span>
        <Pill tone={statusTone(j.status)}>{j.status}</Pill>
        <span className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap">
          {relativeTime(j.started_at).replace(" ago", "")}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-fg-muted">
        <span className="font-mono text-fg-subtle">{j.requested_by}</span>
        <span className="font-mono truncate max-w-[16rem]">{j.command}</span>
        <span className="ml-auto text-fg-subtle">{dur}</span>
        {typeof j.exit_code === "number" && (
          <span className={cn("font-mono", j.exit_code === 0 ? "text-success" : "text-danger")}>exit {j.exit_code}</span>
        )}
      </div>
    </div>
  );
}

function JobDetail({ id, onKilled }: { id: string; onKilled: () => void }) {
  const toast = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: qk.jobs.detail(id),
    queryFn: () => JobsApi.detail(id),
    refetchInterval: (q) => (q.state.data?.meta.status === "running" ? 3000 : false),
  });
  const kill = useMutation({
    mutationFn: () => JobsApi.kill(id),
    onSuccess: (res) => { toast.success(res.already_dead ? "Job already exited" : "Kill signal sent"); onKilled(); },
    onError: (err) => toast.error("Kill failed", String(err)),
  });
  if (isLoading) return <CenteredSpinner />;
  if (error) return <div className="p-5"><Pill tone="danger">Failed to load: {String(error)}</Pill></div>;
  if (!data) return null;
  const { meta, log } = data;
  const isRunning = meta.status === "running";
  const dur = meta.ended_at
    ? formatDuration(new Date(meta.ended_at).getTime() - new Date(meta.started_at).getTime())
    : "running…";
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-fg truncate max-w-[24rem]">{meta.label}</h2>
              <Pill tone={statusTone(meta.status)}>{meta.status}</Pill>
              {typeof meta.exit_code === "number" && (
                <Pill tone={meta.exit_code === 0 ? "success" : "danger"}>exit {meta.exit_code}</Pill>
              )}
              {meta.signal && <Pill tone="warning">signal {meta.signal}</Pill>}
            </div>
            <div className="text-xs text-fg-muted font-mono mt-1 break-all">{meta.command}</div>
            <div className="text-[11px] text-fg-subtle mt-1.5 flex items-center gap-3 flex-wrap">
              <span>{meta.requested_by}</span>
              <span>started {relativeTime(meta.started_at)}</span>
              <span>{dur}</span>
              {meta.pid && <span className="font-mono">pid {meta.pid}</span>}
              {meta.timeout_s && <span>timeout {meta.timeout_s}s</span>}
              {meta.notify_chat && <span>→ {meta.notify_chat.platform}:{meta.notify_chat.chat_id}</span>}
              {meta.total_size !== undefined && <span>{formatBytes(meta.total_size)}</span>}
            </div>
          </div>
          {isRunning && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => { if (confirm(`Cancel job "${meta.label}"${meta.pid ? ` (pid ${meta.pid})` : ""}?`)) kill.mutate(); }}
              loading={kill.isPending}
              title="Send SIGTERM to the job process"
            >
              <Ban className="size-3.5" />
              {meta.pid ? "Cancel" : "Clear"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {log ? (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5">
              output · {log.length.toLocaleString()} chars
            </div>
            <pre className="panel px-3 py-2 text-xs font-mono whitespace-pre-wrap break-words max-h-[60vh] overflow-y-auto text-fg">{log}</pre>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <Terminal className="size-4" />
            <span>No output captured yet.</span>
          </div>
        )}
      </div>
    </div>
  );
}
